import { Link, useLocation } from "react-router-dom";
import { Home, Film, Users, Bell, User as UserIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Facebook-style fixed bottom navigation for mobile.
 * Visible only on screens < lg (1024px). 5 tabs: Home · Reels · Friends · Notifications · Profile.
 * White background, dark accents for the active tab.
 */
export default function BottomNav() {
  const loc = useLocation();
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (!cancelled) setUnread(data.unread || 0);
      } catch { /* noop */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  const items = [
    { to: "/feed", label: "Home", icon: Home, testid: "bottomnav-home" },
    { to: "/clips", label: "Clips", icon: Film, testid: "bottomnav-clips" },
    { to: "/friends", label: "Friends", icon: Users, testid: "bottomnav-friends" },
    { to: "/notifications", label: "Alerts", icon: Bell, testid: "bottomnav-notifications", badge: unread },
    { to: user ? `/profile/${user.id}` : "/auth?mode=login", label: "Profile", icon: UserIcon, testid: "bottomnav-profile" },
  ];

  const isActive = (to) => {
    if (to.startsWith("/profile/")) return loc.pathname.startsWith("/profile/");
    return loc.pathname === to || loc.pathname.startsWith(to + "/");
  };

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-neutral-200 shadow-[0_-2px_10px_rgba(0,0,0,0.06)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="bottom-nav"
    >
      <ul className="flex justify-around items-stretch">
        {items.map(({ to, label, icon: Icon, testid, badge }) => {
          const active = isActive(to);
          return (
            <li key={label} className="flex-1">
              <Link
                to={to}
                data-testid={testid}
                className={`relative flex flex-col items-center justify-center gap-1 py-2.5 transition-colors ${
                  active ? "text-[#6E56A8]" : "text-neutral-500 hover:text-neutral-800"
                }`}
              >
                <div className="relative">
                  <Icon size={22} strokeWidth={active ? 2.2 : 1.75} />
                  {badge > 0 && (
                    <span
                      className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                      data-testid={`${testid}-badge`}
                    >
                      {badge > 9 ? "9+" : badge}
                    </span>
                  )}
                </div>
                <span className="text-[10px] tracking-wide">{label}</span>
                {active && (
                  <span className="absolute top-0 left-1/2 -translate-x-1/2 w-10 h-[3px] rounded-b-sm bg-[#6E56A8]" />
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
